import { useState } from "react";

import { ZodProvider } from "@autoform/zod";
import { ArrowDownToLine, Loader2 } from "lucide-react";

import { LoadingButton } from "@/components/common/loading-button";
import { AutoForm } from "@/components/ui/autoform";
import { injectCustomAutoFormComponents } from "@/components/ui/autoform/components/custom";
import { processAndUploadImage } from "@/lib/imgUtils";
import { useVenueCreateForm } from "@/pages/venues/create/hooks/use-venue-create-form";
import { useVenueCreateFormStore } from "@/pages/venues/create/hooks/use-venue-create-form";
import { mediaVenueSchema } from "@/pages/venues/schemas/venue.create.form";
import { getPhotoUrl } from "@/providers/places-api-provider";

const schemaProvider = new ZodProvider(mediaVenueSchema);

export const VenueMediaForm = () => {
  const {
    id,
    basicInfoStep,
    mediaStep,
    onMediaStepSubmit,
    loading,
    updateMedia,
  } = useVenueCreateForm();
  const [processingIndex, setProcessingIndex] = useState<number | null>(null);

  const googlePhotos = basicInfoStep?.place.photos.map((p: any) =>
    getPhotoUrl(p.name),
  );

  const handleSendToMedia = async (url: string, index: number) => {
    if (!id) return;
    setProcessingIndex(index);
    try {
      const storagePath = `Venues/${id}/${crypto.randomUUID()}.webp`;
      const {
        url: cdnUrl,
        blurhash,
        metadata,
      } = await processAndUploadImage(url, storagePath);
      const currentMedia = mediaStep?.media ?? { photos: [], teasers: [] };
      updateMedia({
        ...mediaStep!,
        media: {
          ...currentMedia,
          photos: [...currentMedia.photos, { url: cdnUrl, blurhash, metadata }],
        },
      });
    } catch (error) {
      console.error("Failed to process image", error);
    } finally {
      setProcessingIndex(null);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="text-md font-medium">Google Photos</div>
      <div className="grid grid-cols-4 md:grid-cols-8 lg:grid-cols-12 gap-1.5 items-start">
        {googlePhotos &&
          googlePhotos.map((url: string, index: number) => (
            <div
              key={index}
              className="relative group overflow-hidden rounded cursor-pointer"
              onClick={() =>
                processingIndex === null && handleSendToMedia(url, index)
              }
            >
              <img
                src={url}
                alt={`Google Photo ${index + 1}`}
                className="w-full h-auto"
              />
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                {processingIndex === index ? (
                  <Loader2 className="h-6 w-6 text-white animate-spin" />
                ) : (
                  <ArrowDownToLine className="h-6 w-6 text-white" />
                )}
              </div>
            </div>
          ))}
      </div>
      <AutoForm
        schema={schemaProvider}
        values={{ ...mediaStep, id }}
        onSubmit={(data, form) => {
          onMediaStepSubmit(data);
        }}
        {...injectCustomAutoFormComponents()}
      >
        <LoadingButton
          isLoading={loading.media === "loading"}
          disabled={loading.media === "loading"}
          variant={loading.media === "error" ? "destructive" : "default"}
        >
          Create
        </LoadingButton>
      </AutoForm>
    </div>
  );
};
